/**
 * Role-Based Access Control (RBAC) Helper Functions
 * للتحقق من صلاحيات المستخدم في التطبيق
 *
 * NOTE: This file provides User-centric helpers (takes User | null).
 * For Role[]-centric helpers, see ./permissions.ts.
 * Both files share the same role constants and scope logic.
 */

export interface Role {
  name: string;
  nameAr: string;
  scope: 'platform' | 'organization' | 'site' | 'zone';
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  orgId?: string;
  roles: Role[]; // Updated to use Role interface
  organization?: {
    id: string;
    name: string;
  };
}

/**
 * الأدوار المتاحة في النظام — مطابقة تماماً لأسماء الأدوار في قاعدة البيانات
 * يجب أن تتطابق هذه القيم مع عمود `name` في جدول `roles`
 */
export const ROLES = {
  // Platform scope (نطاق المنصة)
  PLATFORM_ADMIN: 'مدير المنصة', // Platform Admin - يدير كل المنصة

  // Organization scope (نطاق المؤسسة)
  ADMIN: 'مدير النظام', // Organization Admin
  DEMO_ADMIN: 'Demo Admin', // Demo Organization Admin

  // Zone scope (نطاق المنطقة)
  ZONE_MANAGER: 'مدير المستشفى', // Zone Manager (hospital manager)
  SUPERVISOR: 'مشرف قسم', // Zone Supervisor
  TECHNICIAN: 'فني صيانة', // Technician / Operator
} as const;

const SCOPED_OPERATION_ROLES = [
  ROLES.ZONE_MANAGER,
  ROLES.SUPERVISOR,
  ROLES.TECHNICIAN,
  'Zone Supervisor',
  'Hospital Manager',
  'Technician',
  'operator',
  'Operator',
];

function isScopedOperationsRole(role: any): boolean {
  const roleName = typeof role === 'string' ? role : role?.name;
  const roleScope = typeof role === 'string' ? null : role?.scope;

  return SCOPED_OPERATION_ROLES.includes(roleName) || roleScope === 'zone' || roleScope === 'site';
}

/**
 * فحص إذا كان المستخدم لديه دور محدد
 * @param user المستخدم الحالي
 * @param role الدور المطلوب
 * @returns true إذا كان المستخدم لديه الدور
 */
export function hasRole(user: User | null, role: string): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }

  // Check if roles is array of strings or Role objects
  return user.roles.some((r: any) => {
    if (typeof r === 'string') {
      return r === role;
    }
    // If it's a Role object with name property
    return r.name === role;
  });
}

/**
 * فحص إذا كان المستخدم لديه أي من الأدوار المحددة
 * @param user المستخدم الحالي
 * @param roles قائمة الأدوار المطلوبة
 * @returns true إذا كان المستخدم لديه أي من الأدوار
 */
export function hasAnyRole(user: User | null, roles: string[]): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }

  return roles.some((role) => hasRole(user, role));
}

/**
 * فحص إذا كان المستخدم لديه جميع الأدوار المحددة
 * @param user المستخدم الحالي
 * @param roles قائمة الأدوار المطلوبة
 * @returns true إذا كان المستخدم لديه جميع الأدوار
 */
export function hasAllRoles(user: User | null, roles: string[]): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }

  return roles.every((role) => hasRole(user, role));
}

/**
 * فحص إذا كان المستخدم مدير (admin أو مدير النظام)
 * يشمل أي مستخدم بنطاق organization
 */
export function isAdmin(user: User | null): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) return false;
  return user.roles.some((r: any) => {
    if (isScopedOperationsRole(r)) return false;
    if (typeof r === 'string') return r === ROLES.ADMIN || r === ROLES.DEMO_ADMIN;
    return r.scope === 'organization' || r.name === ROLES.ADMIN || r.name === ROLES.DEMO_ADMIN;
  });
}

/**
 * فحص إذا كان المستخدم مدير المنصة (platform admin)
 */
export function isPlatformAdmin(user: User | null): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) return false;
  return user.roles.some((r: any) => {
    if (typeof r === 'string') return r === ROLES.PLATFORM_ADMIN;
    return r.scope === 'platform' || r.name === ROLES.PLATFORM_ADMIN;
  });
}

/**
 * فحص إذا كان المستخدم مشرف أو مدير على مستوى المنطقة
 * يشمل: مشرف قسم + مدير المستشفى
 */
export function isSupervisor(user: User | null): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) return false;
  return user.roles.some((r: any) => {
    const name = typeof r === 'string' ? r : r.name;
    return name === ROLES.SUPERVISOR || name === ROLES.ZONE_MANAGER;
  });
}

/**
 * فحص إذا كان المستخدم فني صيانة (operator/technician)
 * يشمل: فني صيانة فقط
 */
export function isOperator(user: User | null): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) return false;
  return user.roles.some((r: any) => {
    const name = typeof r === 'string' ? r : r.name;
    return name === ROLES.TECHNICIAN;
  });
}

/**
 * فحص إذا كان المستخدم بمستوى zone (أي دور على مستوى المنطقة)
 * يشمل: فني صيانة + مشرف قسم + مدير المستشفى
 */
export function isZoneLevel(user: User | null): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) return false;
  return user.roles.some((r: any) => isScopedOperationsRole(r));
}

/**
 * @deprecated لا يوجد دور "مشاهد فقط" في النظام حالياً
 * استخدم isZoneLevel() أو isAdmin() حسب الحالة
 */
export function isViewer(_user: User | null): boolean {
  return false;
}

/**
 * فحص إذا كان المستخدم يستطيع إدارة المستخدمين
 * (فقط Admin و Platform Admin)
 */
export function canManageUsers(user: User | null): boolean {
  return isAdmin(user) || isPlatformAdmin(user);
}

/**
 * فحص إذا كان المستخدم يستطيع إدارة الأجهزة
 * Admin + Platform Admin + Zone roles (مشرف + مدير + فني)
 */
export function canManageDevices(user: User | null): boolean {
  return isAdmin(user) || isPlatformAdmin(user) || isZoneLevel(user);
}

/**
 * فحص إذا كان المستخدم يستطيع إدارة المواقع والمناطق
 * (المستخدمون الذين لديهم أدوار بنطاق organization أو platform)
 */
export function canManageSitesAndZones(user: User | null): boolean {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }

  return user.roles.some((role: any) => {
    if (isScopedOperationsRole(role)) {
      return false;
    }

    const roleScope = typeof role === 'string' ? null : role.scope;
    const roleName = typeof role === 'string' ? role : role.name;
    return (
      roleScope === 'organization' ||
      roleScope === 'platform' ||
      roleName === ROLES.ADMIN ||
      roleName === ROLES.DEMO_ADMIN ||
      roleName === ROLES.PLATFORM_ADMIN
    );
  });
}

/**
 * فحص إذا كان المستخدم يستطيع مشاهدة التقارير
 * (الجميع يمكنهم المشاهدة)
 */
export function canViewReports(user: User | null): boolean {
  return user !== null;
}

/**
 * فحص إذا كان المستخدم يستطيع الوصول إلى شاشة التقارير الإدارية
 * لمطابقة الويب: Admin + Platform Admin فقط
 */
export function canCreateReports(user: User | null): boolean {
  return isAdmin(user) || isPlatformAdmin(user);
}

/**
 * فحص إذا كان المستخدم يستطيع تعديل الإعدادات
 * (Admin و Platform Admin فقط)
 */
export function canManageSettings(user: User | null): boolean {
  return isAdmin(user) || isPlatformAdmin(user);
}

/**
 * الحصول على اسم الدور بالعربية
 */
export function getRoleNameArabic(role: string): string {
  const roleMap: Record<string, string> = {
    [ROLES.PLATFORM_ADMIN]: 'مدير المنصة',
    [ROLES.ADMIN]: 'مدير النظام',
    [ROLES.DEMO_ADMIN]: 'مدير تجريبي',
    [ROLES.ZONE_MANAGER]: 'مدير المستشفى',
    [ROLES.SUPERVISOR]: 'مشرف قسم',
    [ROLES.TECHNICIAN]: 'فني صيانة',
  };

  return roleMap[role] || role;
}

/**
 * الحصول على اسم الدور بالإنجليزية
 */
export function getRoleNameEnglish(role: string): string {
  const roleMap: Record<string, string> = {
    [ROLES.PLATFORM_ADMIN]: 'Platform Admin',
    [ROLES.ADMIN]: 'Organization Admin',
    [ROLES.DEMO_ADMIN]: 'Demo Admin',
    [ROLES.ZONE_MANAGER]: 'Zone Manager',
    [ROLES.SUPERVISOR]: 'Zone Supervisor',
    [ROLES.TECHNICIAN]: 'Technician',
  };

  return roleMap[role] || role;
}

/**
 * الحصول على أيقونة الدور
 */
export function getRoleIcon(role: string): string {
  const iconMap: Record<string, string> = {
    [ROLES.PLATFORM_ADMIN]: 'shield-checkmark',
    [ROLES.ADMIN]: 'shield',
    [ROLES.DEMO_ADMIN]: 'shield-outline',
    [ROLES.ZONE_MANAGER]: 'briefcase',
    [ROLES.SUPERVISOR]: 'people',
    [ROLES.TECHNICIAN]: 'build',
  };

  return iconMap[role] || 'person';
}

/**
 * الحصول على لون الدور
 */
export function getRoleColor(role: string): string {
  const colorMap: Record<string, string> = {
    [ROLES.PLATFORM_ADMIN]: '#DC2626', // red
    [ROLES.ADMIN]: '#3949ab', // blue
    [ROLES.DEMO_ADMIN]: '#1565C0', // dark blue
    [ROLES.ZONE_MANAGER]: '#7B1FA2', // purple
    [ROLES.SUPERVISOR]: '#00796B', // teal
    [ROLES.TECHNICIAN]: '#F59E0B', // amber
  };

  return colorMap[role] || '#6B7280';
}
